import { useState, useCallback, useRef, useEffect } from "react";
import { RelayPool } from "applesauce-relay";
import { History, ChevronUp, ExternalLink, Loader2 } from "lucide-react";
import { useNsiteSnapshots } from "@/hooks/useNsiteSnapshots";
import { buildSnapshotUrl, DEFAULT_NSITE_GATEWAY } from "@/lib/nsite";

/** Format a unix timestamp as a short human date. */
function formatDate(ts: number): string {
  const d = new Date(ts * 1000);
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** Format a unix timestamp with time. */
function formatDateTime(ts: number): string {
  const d = new Date(ts * 1000);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Extract a tag value from a nostr event's tags. */
function getTagValue(tags: string[][], name: string): string | undefined {
  return tags.find(([t]) => t === name)?.[1];
}

interface SnapshotVersionsProps {
  pool: RelayPool;
}

export function SnapshotVersions({ pool }: SnapshotVersionsProps) {
  const { snapshots, context, loading } = useNsiteSnapshots(pool);
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  const navigateToSnapshot = useCallback(
    (snapshotId: string) => {
      // Determine which gateway to use.
      // If we're currently on an nsite gateway, replace the subdomain in-place.
      // Otherwise, use the default public gateway (nsite.lol).
      const gateway = context?.gateway ?? DEFAULT_NSITE_GATEWAY;
      const protocol = window.location.protocol;
      const url = buildSnapshotUrl(snapshotId, gateway, protocol);
      window.location.href = url;
    },
    [context],
  );

  // Don't show the component if:
  //  - Already viewing a snapshot (no parent to list siblings from)
  //  - Still loading with no results yet
  //  - No snapshots found
  if (context?.type === "snapshot") return null;
  if (!loading && snapshots.length === 0) return null;

  return (
    <div ref={panelRef} className="absolute bottom-4 left-4 z-20 pointer-events-auto">
      {/* Collapsed button */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-black/40 border border-white/20 text-white/70 hover:text-white hover:bg-black/60 rounded-md backdrop-blur transition-colors select-none"
        title="View older versions"
      >
        {loading ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <History className="h-3.5 w-3.5" />
        )}
        <span>Older versions</span>
        {!loading && snapshots.length > 0 && (
          <span className="ml-0.5 text-white/40">({snapshots.length})</span>
        )}
        <ChevronUp
          className={`h-3 w-3 transition-transform ${open ? "" : "rotate-180"}`}
        />
      </button>

      {/* Expanded panel */}
      {open && (
        <div className="absolute bottom-full mb-2 left-0 w-80 max-h-72 overflow-y-auto rounded-lg bg-slate-900/95 backdrop-blur border border-white/10 shadow-xl">
          <div className="px-3 py-2 border-b border-white/10">
            <p className="text-xs text-white/50 uppercase tracking-wider font-medium">
              Site Snapshots
            </p>
          </div>

          {loading && snapshots.length === 0 ? (
            <div className="flex items-center justify-center py-6 gap-2 text-white/50 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : (
            <div className="flex flex-col">
              {snapshots.map((snap) => {
                const title = getTagValue(snap.tags, "title");
                const desc = getTagValue(snap.tags, "description");
                const xTag = snap.tags.find(
                  ([t, , marker]) => t === "x" && marker === "aggregate",
                );
                const aggregateHash = xTag?.[1];

                return (
                  <button
                    key={snap.id}
                    onClick={() => navigateToSnapshot(snap.id)}
                    className="flex items-start gap-3 px-3 py-2.5 text-left hover:bg-white/5 transition-colors group border-b border-white/5 last:border-b-0"
                    title={`Open snapshot from ${formatDateTime(snap.created_at)}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-white/90 truncate">
                          {title || formatDate(snap.created_at)}
                        </span>
                        {title && (
                          <span className="text-xs text-white/40 shrink-0">
                            {formatDate(snap.created_at)}
                          </span>
                        )}
                      </div>
                      {desc && (
                        <p className="text-xs text-white/40 truncate mt-0.5">{desc}</p>
                      )}
                      {aggregateHash && (
                        <p className="text-xs font-mono text-white/25 truncate mt-0.5">
                          {aggregateHash.slice(0, 12)}…
                        </p>
                      )}
                    </div>
                    <ExternalLink className="h-3.5 w-3.5 text-white/20 group-hover:text-white/50 shrink-0 mt-0.5 transition-colors" />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
